import 'react-activity-calendar/tooltips.css';
import { ActivityCalendar } from 'react-activity-calendar';
import { UTC_DAY_MS } from '@reflex/shared';
import { cn } from '../../lib/utils';
import { formatDateOnly } from '../../lib/format';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

/**
 * Generalized per-day "contribution" calendar heatmap. Promoted from the
 * web app's runs-only `ContributionGrid` so any surface (agent runs, LLM
 * spend, …) can render a year of daily values with its own color scheme
 * and value formatting.
 *
 * The component stays value-agnostic: USD/count formatting is injected by
 * the caller via `formatValue` / `formatTooltip` / `totalLabel`, so
 * `@reflex/ui` never has to know about a specific plugin's domain.
 */

/** Available themes. Maps to `--heatmap-*` CSS-var token sets. */
export type ContributionHeatmapColorScheme = 'runs' | 'spend';

function getColorLevel(value: number, max: number): number {
  if (value === 0) return 0;
  if (max === 0) return 1;
  const ratio = value / max;
  if (ratio <= 0.25) return 1;
  if (ratio <= 0.5) return 2;
  if (ratio <= 0.75) return 3;
  return 4;
}

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

/**
 * Build the per-cell data array the calendar renders for a given year.
 * Exported for unit tests — the function is otherwise an implementation
 * detail of {@link ContributionHeatmap}.
 */
export function buildCalendarData(
  valuesByDay: Record<string, number>,
  year: number,
): { date: string; count: number; level: number }[] {
  // Anchor iteration in UTC so cell keys line up with the server's UTC
  // `YYYY-MM-DD` buckets. A previous version used `new Date(year, 0, 1)`
  // (local midnight) plus `setDate` and then keyed cells via
  // `toISOString().slice(0,10)` — for any viewer east of UTC, the local
  // midnight at start-of-year was the prior UTC day, so every cell ended
  // up one calendar day behind the server's keys and the final UTC day
  // of the year was dropped entirely.
  const startUtcMs = Date.UTC(year, 0, 1);
  const numDays = isLeapYear(year) ? 366 : 365;
  const data: { date: string; count: number; level: number }[] = [];

  for (let i = 0; i < numDays; i++) {
    const key = new Date(startUtcMs + i * UTC_DAY_MS).toISOString().slice(0, 10);
    const count = valuesByDay[key] ?? 0;
    data.push({ date: key, count, level: 0 });
  }

  const maxCount = Math.max(0, ...data.map((c) => c.count));
  data.forEach((c) => {
    c.level = getColorLevel(c.count, maxCount);
  });

  return data;
}

const HEATMAP_THEMES: Record<ContributionHeatmapColorScheme, { light: string[]; dark: string[] }> =
  {
    runs: {
      light: [
        'var(--heatmap-0)',
        'var(--heatmap-1)',
        'var(--heatmap-2)',
        'var(--heatmap-3)',
        'var(--heatmap-4)',
      ],
      dark: [
        'var(--heatmap-0)',
        'var(--heatmap-1)',
        'var(--heatmap-2)',
        'var(--heatmap-3)',
        'var(--heatmap-4)',
      ],
    },
    spend: {
      light: [
        'var(--heatmap-spend-0)',
        'var(--heatmap-spend-1)',
        'var(--heatmap-spend-2)',
        'var(--heatmap-spend-3)',
        'var(--heatmap-spend-4)',
      ],
      dark: [
        'var(--heatmap-spend-0)',
        'var(--heatmap-spend-1)',
        'var(--heatmap-spend-2)',
        'var(--heatmap-spend-3)',
        'var(--heatmap-spend-4)',
      ],
    },
  };

function defaultTotalLabel(total: number, year: number): string {
  return total === 1 ? `1 contribution in ${year}` : `${total} contributions in ${year}`;
}

function defaultFormatTooltip(value: number, dateMs: number): string {
  if (value === 0) return 'No contributions';
  return `${value} contribution${value !== 1 ? 's' : ''} on ${formatDateOnly(dateMs)}`;
}

export interface ContributionHeatmapProps {
  /** Per-day values keyed by UTC `YYYY-MM-DD`. */
  valuesByDay: Record<string, number>;
  /** Calendar year to render. */
  year: number;
  /** Called when the user picks a different year from the year rail. */
  onYearChange?: (year: number) => void;
  className?: string;
  /** Color scheme token set. Defaults to `'runs'` (emerald). */
  colorScheme?: ContributionHeatmapColorScheme;
  /**
   * Format a raw daily value for display in the legend/tooltip. Only used
   * by the default `formatTooltip`/`totalLabel` are overridden; supplied
   * here so callers can inject e.g. USD formatting without rewriting the
   * tooltip/caption text.
   */
  formatValue?: (n: number) => string;
  /** Build the tooltip text for a single day. */
  formatTooltip?: (value: number, dateMs: number) => string;
  /** Build the caption rendered below the calendar (e.g. yearly total). */
  totalLabel?: (total: number, year: number) => string;
}

export function ContributionHeatmap({
  valuesByDay,
  year,
  onYearChange,
  className,
  colorScheme = 'runs',
  formatValue,
  formatTooltip,
  totalLabel = defaultTotalLabel,
}: ContributionHeatmapProps) {
  const data = buildCalendarData(valuesByDay, year);
  const total = data.reduce((a, c) => a + c.count, 0);
  const theme = HEATMAP_THEMES[colorScheme];

  // When a `formatValue` is supplied but no explicit tooltip formatter, fall
  // back to a generic "<formatted value> on <date>" tooltip so injected
  // formatting (e.g. USD) flows through without each caller re-implementing
  // the tooltip text.
  const tooltipText =
    formatTooltip ??
    (formatValue
      ? (value: number, dateMs: number) =>
          value === 0 ? 'No activity' : `${formatValue(value)} on ${formatDateOnly(dateMs)}`
      : defaultFormatTooltip);

  const thisYear = new Date().getFullYear();
  const years = [thisYear, thisYear - 1, thisYear - 2, thisYear - 3];

  return (
    <div className={cn('space-y-3', className)}>
      {onYearChange ? (
        <div className="flex justify-end md:hidden">
          <Select value={String(year)} onValueChange={(value) => onYearChange(Number(value))}>
            <SelectTrigger size="sm" aria-label="Select year" className="h-8">
              <SelectValue>Year: {year}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {years.map((yr) => (
                <SelectItem key={yr} value={String(yr)}>
                  {yr}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}

      <div className="flex w-full items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="max-md:-mx-1 max-md:overflow-x-auto max-md:overscroll-x-contain max-md:pb-1">
            <ActivityCalendar
              data={data}
              theme={theme}
              blockSize={12}
              blockMargin={3}
              blockRadius={3}
              fontSize={10}
              showTotalCount={false}
              labels={{ legend: { less: 'Less', more: 'More' } }}
              tooltips={{
                activity: {
                  text: (activity) => tooltipText(activity.count, Date.parse(activity.date)),
                },
              }}
              weekStart={1}
              className={cn(
                'max-md:w-max max-md:min-w-full',
                'md:w-full md:max-w-full md:[&_svg]:max-w-full',
                '[&_svg]:h-auto',
              )}
            />
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{totalLabel(total, year)}</p>
        </div>
        {onYearChange ? (
          <div
            className="hidden shrink-0 flex-col gap-0.5 md:flex"
            role="group"
            aria-label="Select year"
          >
            {years.map((yr) => (
              <button
                key={yr}
                type="button"
                onClick={() => onYearChange(yr)}
                className={cn(
                  'rounded px-2 py-1 text-xs font-medium transition-colors',
                  yr === year
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted',
                )}
              >
                {yr}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
